import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/types';

export interface AuthLoginResponse {
  access_token: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  name: string;
  fullname?: string;
  refresh_token: string;
  message: string;
  is_2fa_enabled: boolean;
  two_factor_required: boolean;
  temp_token?: string;
  otp_required: boolean;
}

interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  fullname: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  is_2fa_enabled: boolean;
  two_factor_required: boolean;
  temp_token?: string;
}

export type EntraCodeFlow = 'sign_in' | 'sign_up';

/** What the code screen needs; continuation_token goes back with the next call. */
export interface EntraSentCode {
  flow: EntraCodeFlow;
  continuation_token: string;
  code_length: number;
  sent_to: string;
  resend_after: number;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BACKEND_URL,
  }),
  endpoints: (builder) => ({
    userLogin: builder.mutation<ApiResponse<AuthLoginResponse>, LoginRequest>({
      query: (body) => ({
        url: 'api/auth/login',
        method: 'POST',
        body,
      }),
    }),
    verify2FA: builder.mutation<
      LoginResponse,
      { code: string | number; temp_token: string }
    >({
      query: (body) => ({
        url: 'api/auth/verify-2fa/',
        method: 'POST',
        body,
      }),
    }),
    loginWithOtp: builder.mutation<ApiResponse<any>, { phone: string }>({
      query: (body) => ({
        url: 'api/auth/login-with-phone/',
        method: 'POST',
        body,
      }),
    }),
    verifyOtpLogin: builder.mutation<
      ApiResponse<any>,
      { code: string | number; phone: string }
    >({
      query: (body) => ({
        url: 'api/auth/login-with-phone-verify-otp/',
        method: 'POST',
        body,
      }),
    }),
    googleAuthCallback: builder.mutation<ApiResponse<any>, { code: string }>({
      query: (body) => ({
        url: 'api/oauth/google/callback/',
        method: 'POST',
        body,
      }),
    }),
    // Email + one-time code on our own pages; the backend relays to Microsoft.
    entraSendCode: builder.mutation<EntraSentCode, { email: string }>({
      query: (body) => ({
        url: 'api/auth/entra/code/send/',
        method: 'POST',
        body,
      }),
    }),
    entraResendCode: builder.mutation<
      EntraSentCode,
      { flow: EntraCodeFlow; continuation_token: string }
    >({
      query: (body) => ({
        url: 'api/auth/entra/code/resend/',
        method: 'POST',
        body,
      }),
    }),
    entraVerifyCode: builder.mutation<
      ApiResponse<AuthLoginResponse>,
      {
        flow: EntraCodeFlow;
        continuation_token: string;
        code: string;
        email: string;
        application_id?: string | number;
        application_secret?: string;
        referral_code?: string;
      }
    >({
      query: (body) => ({
        url: 'api/auth/entra/code/verify/',
        method: 'POST',
        body,
      }),
    }),
    entraSession: builder.mutation<
      ApiResponse<AuthLoginResponse>,
      {
        id_token: string;
        application_id?: string | number;
        application_secret?: string;
        referral_code?: string;
      }
    >({
      query: (body) => ({
        url: 'api/auth/entra/session/',
        method: 'POST',
        body,
      }),
    }),
  }),
});
export const {
  useUserLoginMutation,
  useVerify2FAMutation,
  useLoginWithOtpMutation,
  useVerifyOtpLoginMutation,
  useGoogleAuthCallbackMutation,
  useEntraSendCodeMutation,
  useEntraResendCodeMutation,
  useEntraVerifyCodeMutation,
  useEntraSessionMutation,
} = authApi;
